"use client";

import TestRunner from "@/components/teste/TestRunner";
import type { PublicTherapistSummary } from "@/actions/getTherapists";
import {
  depresieConfig,
  PHQ9_SELF_HARM_ITEM_INDEX,
  type DepressionCategory,
} from "@/data/tests/depresie";
import { captureDepresieLead, submitDepresieTest } from "./actions";

/**
 * The PHQ-9 wired into the shared runner.
 *
 * A client component on purpose: the config holds functions, which a server
 * page cannot hand to a client component, so the config is imported here and
 * the page passes only the therapist list.
 *
 * `crisis` is the reason this wrapper is more than a copy of the anxiety one.
 * Item 9 asks about thoughts of death or self-harm, and any answer other than
 * „Deloc” shows the crisis lines at the top of the result, whatever the total.
 * A score of 3 with a 1 on item 9 is not a "minimal" result to be read calmly.
 */

const LABELS: Record<DepressionCategory, string> = Object.fromEntries(
  depresieConfig.bands.map((b) => [b.category, b.label]),
) as Record<DepressionCategory, string>;

export default function DepresieRunner({
  therapists,
}: {
  therapists: PublicTherapistSummary[];
}) {
  return (
    <TestRunner<DepressionCategory>
      config={depresieConfig}
      submit={submitDepresieTest}
      capture={captureDepresieLead}
      therapists={therapists}
      urgentCategories={["severe"]}
      crisis={{
        itemIndex: PHQ9_SELF_HARM_ITEM_INDEX,
        minValue: 1,
        lead: "La ultima întrebare ai spus că, în ultimele două săptămâni, ai avut gânduri că ar fi mai bine să nu mai trăiești sau gânduri de a-ți face rău. Asta contează mai mult decât scorul total, oricât de mic ar fi.",
      }}
      zenyra={{
        topic: "depresie",
        prefill: (score, category) =>
          `Am facut testul de depresie (PHQ-9) pe Mindtale si scorul meu este ${score}/27 (${LABELS[category] ?? category}). Vreau sa povestim despre ce inseamna pentru mine si cum pot continua.`,
      }}
      emailCopy={{
        headline: "Vreau să primesc rezultatul și câțiva pași concreți",
        body: "Rezultatul pe email, plus câteva resurse scurte despre depresie și ce poți face de azi. Fără spam, te dezabonezi oricând.",
        submitLabel: "Trimite-mi rezultatul",
      }}
    />
  );
}
